import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Cloud, Server, ShieldCheck, Database, Key, Monitor } from "lucide-react";

const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${i <= rating ? "text-yellow-500" : "text-gray-300"} inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2 border border-gray-300">
    <ShieldCheck className="w-3 h-3 mr-1 text-blue-600" />
    Verified
  </span>
);

const ToolsYouWillMaster = () => {
  const tools = [
    { name: 'Azure Portal', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/Microsoft_Azure.svg/1200px-Microsoft_Azure.svg.png' },
    { name: 'Virtual Machines', logo: 'https://cdn.worldvectorlogo.com/logos/azure-1.svg' }, // Generic Azure
    { name: 'Active Directory', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/Microsoft_logo.svg/2048px-Microsoft_logo.svg.png' },
    { name: 'Blob Storage', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg' },
    { name: 'SQL Database', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/microsoftsqlserver/microsoftsqlserver-plain.svg' },
    { name: 'PowerShell', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/powershell/powershell-original.svg' },
    { name: 'DevOps', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azuredevops/azuredevops-original.svg' },
    { name: 'VNet', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg' },
  ];

  return (
    <section className="py-20 bg-gray-100">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-black font-aligran">
          Azure Tools You Will <span className="text-blue-600">Master</span>
        </h2>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-8 mb-8">
          {tools.map((tool, i) => (
            <motion.div
              key={i}
              className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-105"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <img src={tool.logo} alt={tool.name} className="w-16 h-16 mb-2 object-contain" />
              <p className="text-sm font-semibold text-black mt-2">{tool.name}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AzureCourse = () => {
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [formData, setFormData] = useState({ firstName: "", businessEmail: "", phone: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Inquiry Sent!");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % 5);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const testimonials = [
    { id: 1, name: "Karan Singh", review: "Microsoft Azure is huge, but the modules on Active Directory and VMs were explained perfectly.", rating: 5, profileImg: "https://randomuser.me/api/portraits/men/44.jpg", source: "Google Reviews" },
    { id: 2, name: "Anita Roy", review: "I work in an enterprise that uses Windows Servers. Learning Azure was the natural next step. Great course.", rating: 5, profileImg: "https://randomuser.me/api/portraits/women/32.jpg", source: "LinkedIn" },
    { id: 3, name: "Suresh Patel", review: "The lab sessions for deploying Virtual Machines and configuring VNets were very helpful.", rating: 4, profileImg: "https://randomuser.me/api/portraits/men/55.jpg", source: "Website Feedback" },
    { id: 4, name: "Deepa Verma", review: "Azure DevOps module was a bonus. Now I understand CI/CD pipelines in Azure.", rating: 5, profileImg: "https://randomuser.me/api/portraits/women/65.jpg", source: "Google Reviews" },
    { id: 5, name: "Rahul Das", review: "Excellent faculty. They prepared me for the AZ-104 Admin exam.", rating: 4, profileImg: "https://randomuser.me/api/portraits/men/15.jpg", source: "LinkedIn" },
  ];

  const currentVisibleTestimonials = [
    testimonials[currentTestimonialIndex],
    testimonials[(currentTestimonialIndex + 1) % 5],
    testimonials[(currentTestimonialIndex + 2) % 5],
  ];

  const curriculum = [
    { title: "Module 1: Introduction to Azure", description: "Cloud concepts, Azure Portal walkthrough, Subscription & Cost Management." },
    { title: "Module 2: Identity (Azure AD)", description: "Managing Users, Groups, Devices, and Role-Based Access Control (RBAC)." },
    { title: "Module 3: Azure Virtual Machines", description: "Deploying Windows/Linux VMs, Availability Sets, and Scaling." },
    { title: "Module 4: Azure Networking", description: "Virtual Networks (VNet), Subnets, NSGs, Peering, and VPN Gateway." },
    { title: "Module 5: Azure Storage", description: "Blob Storage, File Shares, Tables, Queues, and Storage Explorer." },
    { title: "Module 6: Azure App Services", description: "Hosting Web Apps, Deployment Slots, and Scaling Web Apps." },
    { title: "Module 7: Azure SQL Database", description: "Deploying SQL Databases, Migration, and Backup strategies." },
    { title: "Module 8: Monitoring & Security", description: "Azure Monitor, Alerts, Network Watcher, and Security Center." },
    { title: "Module 9: Azure DevOps Basics", description: "Introduction to Azure Repos, Pipelines, and CI/CD concepts." },
  ];

  const highlights = [
    { title: 'Microsoft Power', desc: 'Enterprise favorite cloud.', img: 'https://images.unsplash.com/photo-1633419461186-7d40a2e12e7e?q=80&w=2070' },
    { title: 'Certification', desc: 'AZ-104 Admin Prep.', img: 'https://images.unsplash.com/photo-1589330273594-fade1ee91647?q=80&w=2070' },
    { title: 'Active Directory', desc: 'Identity Management.', img: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=2070' },
    { title: 'Virtual Machines', desc: 'Windows & Linux VMs.', img: 'https://images.unsplash.com/photo-1597852074816-d933c722b99b?q=80&w=2070' },
    { title: 'Hybrid Cloud', desc: 'Connect On-Prem to Cloud.', img: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?q=80&w=2070' },
    { title: 'Storage', desc: 'Blob & File Storage.', img: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070' },
    { title: 'Expert Faculty', desc: 'Microsoft Certified Trainers.', img: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070' },
    { title: 'Job Ready', desc: 'High demand in MNCs.', img: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=2069' },
    { title: 'DevOps Intro', desc: 'Basics of Azure DevOps.', img: 'https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?q=80&w=2088' },
  ];

  return (
    <div className="bg-white text-gray-900 font-sans">
      <Helmet>
        <title>Microsoft Azure Course | Cloud Administrator Training</title>
        <meta name="description" content="Master Microsoft Azure. Learn AZ-104 Administration, Virtual Machines, and Active Directory." />
      </Helmet>

      {/* Hero */}
      <section className="relative h-[550px] flex items-center bg-black">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1633419461186-7d40a2e12e7e?q=80&w=2070"
            alt="Microsoft Azure"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-blue-900/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center w-full pt-20">
          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }}>
            <span className="inline-block px-3 py-1 mb-4 border border-blue-500 rounded-full bg-blue-900/30 text-blue-400 text-sm font-semibold">
              MICROSOFT CERTIFIED
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white font-aligran">
              Master <span className="text-blue-500 block">Microsoft Azure</span>
            </h1>
            <p className="text-xl text-gray-300 mt-6 mb-8">
              Become a Cloud Administrator. Learn to manage identity, storage, and compute resources on the Microsoft Cloud.
            </p>
            <button
              onClick={() => document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-10 rounded-lg shadow-lg hover:scale-105 transition"
            >
              Start Cloud Career
            </button>
          </motion.div>
          <motion.div className="flex justify-end">
            <Cloud size={220} className="text-blue-500 opacity-80 drop-shadow-lg" />
          </motion.div>
        </div>
      </section>

      {/* Why Learn */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50 text-center">
        <h2 className="text-4xl font-extrabold mb-14 font-aligran">
          Why Learn <span className="text-blue-600">Azure?</span>
        </h2>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-8">
          <div className="p-8 border border-gray-100 rounded-2xl bg-white shadow-md hover:-translate-y-2 transition duration-300">
            <Key className="mx-auto mb-4 text-blue-600" size={40} />
            <h3 className="font-bold text-xl mb-3">Enterprise Choice</h3>
            <p className="text-gray-600">Most Fortune 500 companies use Azure because of its integration with Windows Server & AD.</p>
          </div>
          <div className="p-8 border border-gray-100 rounded-2xl bg-white shadow-md hover:-translate-y-2 transition duration-300">
            <Monitor className="mx-auto mb-4 text-purple-600" size={40} />
            <h3 className="font-bold text-xl mb-3">Hybrid Cloud</h3>
            <p className="text-gray-600">Azure offers the best hybrid cloud capabilities, connecting on-premise data centers seamlessly.</p>
          </div>
          <div className="p-8 border border-gray-100 rounded-2xl bg-white shadow-md hover:-translate-y-2 transition duration-300">
            <ShieldCheck className="mx-auto mb-4 text-green-600" size={40} />
            <h3 className="font-bold text-xl mb-3">Trust & Compliance</h3>
            <p className="text-gray-600">Microsoft invests billions in security, making Azure one of the most trusted clouds.</p>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
            Course <span className="text-blue-600">Highlights</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {highlights.map((h, i) => (
              <div
                key={i}
                className="bg-white rounded-xl shadow-md overflow-hidden flex h-40 hover:shadow-xl transition hover:-translate-y-1"
              >
                <div className="w-1/2 p-4 flex flex-col justify-center">
                  <h3 className="font-bold text-lg">{h.title}</h3>
                  <p className="text-sm text-gray-600">{h.desc}</p>
                </div>
                <div className="w-1/2 relative">
                  <img src={h.img} alt={h.title} className="w-full h-full object-cover" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 font-aligran">
            Detailed <span className="text-blue-600">Curriculum</span>
          </h2>
          <div className="space-y-4">
            {curriculum.map((m, i) => (
              <details key={i} className="group border rounded-lg bg-gray-50">
                <summary className="cursor-pointer px-6 py-4 font-semibold flex justify-between">
                  {m.title}
                  <span className="group-open:rotate-180 transition">▼</span>
                </summary>
                <div className="px-6 py-4 border-t text-gray-700">{m.description}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Tools */}
      <ToolsYouWillMaster />

      {/* FAQ & Form */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl font-bold mb-8 font-aligran">FAQ</h2>
          <div className="space-y-4">
            <details className="bg-gray-50 border p-4 rounded-lg">
              <summary className="font-bold">AWS or Azure?</summary>
              <p className="mt-2 text-sm">If you work with Microsoft products (.NET, Windows Server), Azure is the better choice.</p>
            </details>
            <details className="bg-gray-50 border p-4 rounded-lg">
              <summary className="font-bold">Do I need coding?</summary>
              <p className="mt-2 text-sm">No, Azure Administration (AZ-104) is infrastructure-focused and requires very little coding.</p>
            </details>
          </div>
        </div>
        <div className="md:col-span-1" id="enquiry-form">
          <div className="bg-gray-100 border border-blue-300 rounded-xl p-6 shadow-xl sticky top-10">
            <h3 className="text-2xl font-bold mb-2 text-center text-blue-600">Book Seat</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                className="w-full border p-3 rounded"
                placeholder="Name"
                name="firstName"
                onChange={handleInputChange}
              />
              <input
                className="w-full border p-3 rounded"
                placeholder="Email"
                name="businessEmail"
                onChange={handleInputChange}
              />
              <input
                className="w-full border p-3 rounded"
                placeholder="Phone"
                name="phone"
                onChange={handleInputChange}
              />
              <button className="w-full bg-blue-600 text-white font-bold py-3 rounded hover:bg-blue-700 transition">
                ENQUIRE
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-gray-50 py-20 border-t">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 font-aligran">
            Success <span className="text-blue-600">Stories</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {currentVisibleTestimonials.map((t) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-white p-6 rounded-xl border shadow-sm"
                >
                  <div className="flex items-center mb-4">
                    <img
                      src={t.profileImg}
                      className="w-12 h-12 rounded-full border-2 border-blue-600 mr-3"
                      alt={t.name}
                    />
                    <div>
                      <h4 className="font-bold">{t.name}</h4>
                      <span className="text-xs text-gray-500">{t.source}</span>
                    </div>
                  </div>
                  <StarRating rating={t.rating} />
                  <p className="text-gray-700 text-sm mt-2">"{t.review}"</p>
                  <VerifiedBadge />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AzureCourse;